import type { Tenant } from '@/core/tenant'

// every 3 columns is a new row
export const permissionGridLayout = [
	['sessions', 'campaigns', 'marketing'],
	['receipts', 'microsites', 'referrals'],
	['journeyIntelligence', 'rewards'],
]

export const rbacResourceMap: any = {
	settings: ['settings', 'administrators', 'integrations', 'tenants'],
	receipts: ['receipts', 'products', 'rule_sets', 'formats', 'synonyms', 'merchant_listings', 'merchants'],
	marketing: ['marketing_campaigns', 'webhooks', 'segments', 'email-connections', 'messaging-connections'],
	audiences: ['audiences'],
	rewards: [
		'tenants',
		'venues',
		'rewards',
		'event_types',
		'event_reactors',
		'events',
		'users',
		'settings',
		'tiers',
		'consent_types',
		'audiences',
		'code_pools',
		'points_liability_criteria',
		'badges',
		'tiles',
		'tiles/sections',
		'tiles/groups',
		'campaigns',
		'leaderboards',
	],
	teams: ['teams'],
	referrals: ['referral_campaigns'],
	sessions: ['token_profiles', 'user_sessions'],
	microsites: ['microsites'],
	journeyIntelligence: ['intelligence'],
}

export const permissionLabelOverride: any = {
	campaigns: 'Campaigns',
	rewards: 'Core',
	journeyIntelligence: 'Intelligence',
}

export const permissionMap: any = {
	receipts: ['receipts', 'products', 'merchants'],
	marketing: ['campaigns', 'webhooks', 'segments', 'email-connections', 'messaging-connections'],
	rewards: [
		'tenants',
		'venues',
		'rewards',
		'event_types',
		'event_reactors',
		'events',
		'users',
		'settings',
		'tiers',
		'consent_types',
		'audiences',
		'integrations',
		'teams',
		'points_liability_criteria',
		'badges',
		'leaderboards',
	],
	referrals: ['referral_campaigns'],
	sessions: ['token_profiles', 'user_sessions'],
	microsites: ['microsites'],
	journeyIntelligence: ['intelligence'],
}

export const permissions = {
	campaigns: [{ label: 'Campaigns', value: 'admin:campaigns' }],
	receipts: [
		{ label: 'Receipts', value: 'admin:receipts' },
		{ label: 'Products', value: 'admin:products' },
		{ label: 'Merchants', value: 'admin:merchants' },
	],
	marketing: [
		{ label: 'Segments', value: 'admin:segments' },
		{ label: 'Webhooks', value: 'admin:webhooks' },
	],
	rewards: [
		{ label: 'Tenants', value: 'admin:tenants' },
		{ label: 'Events', value: 'admin:events' },
		{ label: 'Event Types', value: 'admin:event_types' },
		{ label: 'Reactors', value: 'admin:event_reactors' },
		{ label: 'Rewards', value: 'admin:rewards' },
		{ label: 'Users', value: 'admin:users' },
		{ label: 'Venues', value: 'admin:venues' },
		{ label: 'Settings', value: 'admin:settings' },
		{ label: 'Tiers', value: 'admin:tiers' },
		{ label: 'Consent Types', value: 'admin:consent_types' },
		{ label: 'Audiences', value: 'admin:audiences' },
		{ label: 'Integrations', value: 'admin:integrations' },
		{ label: 'Teams', value: 'admin:teams' },
		{
			label: 'Points Liability',
			value: 'admin:points_liability_criteria',
		},
		{ label: 'Badges', value: 'admin:badges' },
		{ label: 'Leaderboards', value: 'admin:leaderboards' },
	],
	referrals: [{ label: 'Referral Campaigns', value: 'admin:referral_campaigns' }],
	sessions: [
		{ label: 'Partial Users', value: 'admin:token_profiles' },
		{ label: 'User Sessions', value: 'admin:user_sessions' },
	],
	microsites: [{ label: 'Microsites', value: 'admin:microsites' }],
	journeyIntelligence: [{ label: 'Intelligence', value: 'admin:intelligence' }],
}

export const checkValidPermissionsFromUrl = (tenantFeatures: any, pathName: any) => {
	let validPermission = null

	Object.keys(permissions).map((k) => {
		if (tenantFeatures[k]) {
			// @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
			return permissions[k].map((obj: any) => {
				if (obj.value.includes(pathName.substring(1).split('/')[0])) {
					validPermission = obj
				}

				return obj
			})
		}

		return k
	})

	return validPermission
}

export const accessTokenToRbacPermissions = (scope: string, tenant?: Tenant | null) => {
	const availableResources: Array<any> = ['admin/actions', 'metadata']

	if (tenant) {
		const { features } = tenant
		const rbacResourceKeys = [...Object.keys(features), 'settings']

		const featureKeys = rbacResourceKeys.filter((permission) => {
			// Certain permissions rely on tenant features to be enabled.
			switch (permission) {
				case 'intelligence':
					return features.journeyIntelligence
				case 'sessions':
				case 'referrals':
				case 'teams':
					return features.rewards && features[permission]
				case 'products':
					return features.receipts && features[permission]
				case 'settings':
					return true
				default:
					return features[permission]
			}
		})

		availableResources.push(
			...featureKeys.flatMap((resource: any) => {
				if (resource === 'products') {
					return []
				}
				return rbacResourceMap[resource]
			})
		)
	}

	const splitScope = scope.split(' ').filter((p: any) => p.includes(':'))
	// Work-around, permissions are missing from auth0 !!
	// TODO: Add permissions to auth0 ?
	const auth0Permissions: any[] = ['admin:admin/actions']

	splitScope.forEach((p: any) => {
		const [level, resource] = p.split(':')
		// Read must be manipulated as RA expects show & list, not read
		if (level === 'read') {
			if (resource === 'tenants') {
				// No admin permission, so add it if user has tenants
				auth0Permissions.push('list:administrators')
				auth0Permissions.push('show:administrators')
			}

			if (resource === 'campaigns') {
				auth0Permissions.push(`list:marketing_campaigns`)
				auth0Permissions.push(`show:marketing_campaigns`)
			}

			if (resource === 'products') {
				// No synonyms & merchant_listings permission, so add it if user has products
				auth0Permissions.push('list:synonyms')
				auth0Permissions.push('show:merchant_listings')
			}

			if (resource === 'receipts') {
				// No rule_sets & formats permission, so add it if user has receipts
				auth0Permissions.push('list:rule_sets')
				auth0Permissions.push('list:formats')
				auth0Permissions.push('show:rule_sets')
				auth0Permissions.push('show:formats')
				auth0Permissions.push('list:metadata')
			}

			if (resource === 'integrations') {
				auth0Permissions.push('list:email-connections')
				auth0Permissions.push('list:messaging-connections')
			}

			auth0Permissions.push(`list:${resource}`)
			auth0Permissions.push(`show:${resource}`)
			return
		} else if (level === 'update') {
			if (resource === 'settings') {
				auth0Permissions.push('edit:administrators')
			}

			if (resource === 'campaigns') {
				auth0Permissions.push(`edit:marketing_campaigns`)
			}

			if (resource === 'products') {
				auth0Permissions.push('edit:synonyms')
				auth0Permissions.push('edit:merchant_listings')
			}

			if (resource === 'receipts') {
				auth0Permissions.push('edit:rule_sets')
				auth0Permissions.push('edit:formats')
				auth0Permissions.push('edit:metadata')
			}

			if (resource === 'integrations') {
				auth0Permissions.push('edit:email-connections')
				auth0Permissions.push('edit:messaging-connections')
			}

			auth0Permissions.push(`edit:${resource}`)
			return
		} else {
			// Mimic the permission level of the *parent* permission
			// ONLY APPLIES TO PERMISSIONS THAT DON'T HAVE THEIR OWN
			// TOGGLE.
			if (resource === 'settings') {
				auth0Permissions.push(`${level}:administrators`)
			}

			if (resource === 'products') {
				auth0Permissions.push(`${level}:synonyms`)
				auth0Permissions.push(`${level}:merchant_listings`)
			}

			if (resource === 'receipts') {
				auth0Permissions.push(`${level}:rule_sets`)
				auth0Permissions.push(`${level}:formats`)
				auth0Permissions.push(`${level}:metadata`)
			}

			if (resource === 'rewards') {
				auth0Permissions.push(`${level}:code_pools`)
				auth0Permissions.push(`${level}:tiles`)
				auth0Permissions.push(`${level}:tiles/sections`)
				auth0Permissions.push(`${level}:tiles/groups`)
			}

			if (resource === 'campaigns') {
				auth0Permissions.push(`${level}:marketing_campaigns`)
			}

			if (resource === 'integrations') {
				auth0Permissions.push(`${level}:email-connections`)
				auth0Permissions.push(`${level}:messaging-connections`)
			}

			auth0Permissions.push(p)
		}
	})

	// TODO: Move to utils?
	const resourceTransform = (res: string) => {
		// Some resources don't match their corresponding auth0 permission
		switch (res) {
			case 'microsites':
				return 'microsite'
			case 'audiences':
				return 'reward_audiences'
			default:
				return res
		}
	}

	const rbacPermissions: any[] = []
	auth0Permissions.forEach((p) => {
		const [level, resource] = p.split(':')

		const isAdminLevel = level === 'admin'

		// If the tenant does not have x enabled, deny it. but make exception
		// for tenants as it is not a tenant-specific feature
		if (availableResources.length && !availableResources.includes(resource) && resource !== 'tenants') {
			rbacPermissions.push({
				action: '*',
				resource: resourceTransform(resource),
				type: 'deny',
			})
			return
		}

		if (isAdminLevel) {
			rbacPermissions.push({
				action: '*',
				resource: resourceTransform(resource),
			})
			return
		} else {
			// Handle duplicate resources, create:rewards, update:rewards etc.
			const duplicateIndex = rbacPermissions.findIndex((p) => p.resource === resourceTransform(resource))
			if (duplicateIndex >= 0 && rbacPermissions[duplicateIndex].action === '*') {
				return
			} else if (duplicateIndex >= 0) {
				const duplicatePermission = rbacPermissions[duplicateIndex]
				rbacPermissions[duplicateIndex] = {
					action: [...duplicatePermission.action, level],
					resource: resourceTransform(resource),
				}
				return
			} else {
				rbacPermissions.push({
					action: [level],
					resource: resourceTransform(resource),
				})
				return
			}
		}
	})

	return rbacPermissions
}
